<template>
	<div
		ref="cardRoot"
		class="relative"
		data-test-id="property-card"
		v-bind="attrsWithoutDataTest"
	>
		<component :is="wrapperComponent" v-bind="wrapperProps">
			<Card
				:class="
					cn(
						propertyCardBodyVariants({ size, variant }),
						props.class,
						'cursor-pointer min-h-fit'
					)
				"
				@click="handlePropertyClicked"
			>
				<PropertyCardHeader
					v-bind="forwardedProps"
					:is-mouse-over-card-header="isMouseOverCardHeader"
					:card-position="cardPosition"
					:section="section"
					:hide-favorite-button="true"
					:is-property-active="isPropertyActive"
					class="group/card z-10"
					@mouseover="handleMouseOver"
					@mouseleave="handleMouseLeave"
				>
					<PropertyCardContent
						v-if="isPropertyActive"
						v-bind="forwardedProps"
						@mouseover="handleMouseOver"
						@mouseleave="handleMouseLeave"
					/>

					<PropertyCardFooter
						v-if="isFullFace"
						v-bind="forwardedProps"
						:as-tags="asTags"
					/>
				</PropertyCardHeader>

				<PropertyCardFooter
					v-if="!isFullFace"
					v-bind="forwardedProps"
					:size="footerSize"
				/>
			</Card>
		</component>

		<!-- This button needs to be outside the NuxtLink because the link opens a new tab when clicked-->
		<!-- ClientOnly ensures favorite state is only rendered on client to improve cache hit ratio -->
		<!-- SSR: Shows inactive star, Client: Updates to real state -->
		<!-- v-if must be outside ClientOnly to ensure matching DOM structure during hydration -->
		<div v-if="isValidData" class="absolute right-4 top-4 z-30">
			<ClientOnly>
				<template #fallback>
					<FavoriteButton
						:is-favorite="false"
						:property-id="property.id"
						:commercial-id="property.commercialId"
					/>
				</template>
				<FavoriteButton
					:key="`favorite-${property.id}-${isUserLoggedIn}`"
					v-model:is-favorite="isFavorite"
					:property-id="property.id"
					:commercial-id="property.commercialId"
					@toggle="toggleFavorite"
					@error="handleWishlistError"
				/>
			</ClientOnly>
		</div>

		<div v-if="!isPropertyActive" class="absolute left-4 top-4 z-30">
			<Badge variant="white" class="text-xs" data-testid="inactive-badge">
				Indisponível
			</Badge>
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { PropertyCardProps } from '.';
import type { ImageClickedEvent } from '../Card/CardCarousel';
import type { SectionName } from '~/types/mixpanel';

import { cn, hasShortsVideo } from '@/utils';
import { NuxtLink } from '#components';
import { useEvents } from '~/composables/events/useEvents';
import { useImpressionBatch } from '~/composables/useImpressionBatch';
import { useListingEventTracking } from '~/composables/useListingEventTracking';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isNumber } from '~~/utils';
import { useForwardProps } from 'reka-ui';
import { computed, ref } from 'vue';

import { FavoriteButton } from '../FavoriteButton';
import { Card } from '../ui/card';
import {
	propertyCardBodyVariants,
	PropertyCardContent,
	PropertyCardFooter,
	PropertyCardHeader
} from './index';

defineOptions({
	inheritAttrs: false
});

const attrs = useAttrs();

const attrsWithoutDataTest = computed(() => {
	const { 'data-test': _, ...rest } = attrs;
	return rest;
});

const props = withDefaults(defineProps<PropertyCardProps>(), {
	variant: 'whiteBackground',
	size: 'sm',
	disableLink: false,
	showActions: false
});

const isFullFace = computed(() => props.variant === 'fullFace');

const isLinkEnabled = computed(() => {
	const { commercialId, slug } = props.property ?? {};
	return !props.disableLink && commercialId && slug;
});

// Dynamic wrapper component: NuxtLink when enabled, div when disabled
const wrapperComponent = computed(() =>
	isLinkEnabled.value ? NuxtLink : 'div'
);

// Props for the wrapper component
const wrapperProps = computed(() => {
	if (!isLinkEnabled.value) {
		return {};
	}
	return {
		to: `/imovel/${props.property.commercialId}/${props.property.slug}`,
		target: '_blank',
		rel: 'noopener noreferrer'
	};
});

const route = useRoute();
const { trackPropertyCardClicked } = useEvents();
const { trackClick } = useListingEventTracking();
const { loggedIn: isUserLoggedIn } = useUserSession();

const isMouseOverCardHeader = ref(false);

const eventData = {
	currentPage: route.fullPath,
	tagExclusive: props.property.isExclusive,
	companyName: props.property.company?.name,
	agentName: props.property.agent?.name,
	clickCard: true,
	videoShorts: hasShortsVideo(props.property)
};

// Pass the real values to the composable
const propertyId = computed(() => props.property.id);
const commercialId = computed(() => props.property.commercialId);
const initialFavoriteState = computed(
	() => props.property?.inUserWishlist ?? false
);

// useToggleFavoriteSync is synchronous - no await needed
// This prevents blocking SSR rendering for each property card
const {
	isFavorite,
	handleToggleFavorite: toggleFavorite,
	isValidData
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	eventData,
	props.section,
	initialFavoriteState
);

function handleMouseOver() {
	isMouseOverCardHeader.value = true;
}
function handleMouseLeave() {
	isMouseOverCardHeader.value = false;
}

const delegatedProps = computed(() => {
	const {
		class: _,
		section: _s,
		cardPosition: _c,
		asTags: _a,
		disableLink: _d,
		...delegated
	} = props;

	return delegated;
});

const forwardedProps = useForwardProps(delegatedProps);

const section = computed<SectionName | undefined>(() => {
	if (props.section) {
		return props.section;
	}

	if (route.fullPath.startsWith('/venda/mapa-de-imoveis')) {
		return 'listed properties';
	}

	return undefined;
});

const footerSize = computed(() => {
	if (props.showActions) {
		return 'lg';
	}
	return 'sm';
});

const isPropertyActive = computed(
	() => props.property.publicationStatus === 'published'
);

const cardRoot = ref<HTMLElement | null>(null);

const shouldTrackImpressions = computed(
	() =>
		isLinkEnabled.value &&
		!!props.trackingContext &&
		isNumber(props.positionInList)
);

// Nulling the target unobserves the card and cancels any pending 500ms timer.
const impressionTarget = computed<HTMLElement | null>(() =>
	shouldTrackImpressions.value ? cardRoot.value : null
);

useImpressionBatch(impressionTarget, () => ({
	propertyId: props.property.id,
	trackingContext: props.trackingContext!,
	positionInList: props.positionInList!
}));

const trackingSharedProps = computed(() => ({
	[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
	[MixpanelProps.CURRENT_PAGE]: route.fullPath,
	[MixpanelProps.SECTION_NAME]: section.value || '',
	[MixpanelProps.CARD_POSITION]: isNumber(props.cardPosition)
		? props.cardPosition
		: undefined,
	[MixpanelProps.CARD_TYPE]:
		props.variant === 'fullFace' ? 'full face' : 'regular card',
	[MixpanelProps.COMPANY_NAME]: props.property.company.name,
	[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive,
	[MixpanelProps.AGENT_NAME]: props.property.agent.name
}));

function handlePropertyClicked(event: ImageClickedEvent) {
	const clickedAt = new Date().toISOString();

	trackPropertyCardClicked({
		[MixpanelProps.DESTINATION_URL]: `/imovel/${props.property.commercialId}/${props.property.slug}`,
		[MixpanelProps.PHOTO_URL]: event.photoUrl,
		[MixpanelProps.PHOTO_POSITION]: event.photoPosition,
		...trackingSharedProps.value
	});

	emitListingClickEvent(clickedAt);
}

function emitListingClickEvent(clickedAt: string) {
	if (!isLinkEnabled.value) return;
	if (!props.trackingContext || !isNumber(props.positionInList)) return;

	const position = props.positionInList + 1;

	trackClick({
		propertyId: props.property.id,
		clickedAt,
		context: {
			...props.trackingContext,
			url: route.path,
			position
		}
	});
}

function handleWishlistError(error: string) {
	// To do on toast tasks
}
</script>
